import { IFluxBotModel } from '../data/models/flux-bot';
import { IGetAllResponse, IGetOneResponse } from '../util/Types/Generic';
import { ApiService } from './ApiService';
// import { User } from '../Utilis/Types/User';
export class FlowService extends ApiService {
  static pathName = '/flow';
  static edgePathName = '/edge';

  static getFlows = (signal?: AbortSignal): Promise<IGetAllResponse<any>> => {
    // return this.get(`${this.pathName}?page=1&pageSize=10000`, signal);
    return this.get(`${this.pathName}`);
  };

  static getFlow = (
    id: string,
    signal?: AbortSignal,
    historyId?: string,
    versionId?: string,
  ): Promise<IGetOneResponse<any>> => {
    const params: Record<string, string> = {};
    if (historyId) params.historyId = historyId;
    if (versionId) params.versionId = versionId;
    return this.get(
      `${this.pathName}/${id}`,
      undefined,
      signal,
      Object.keys(params).length ? params : undefined,
    );
  };

  // Lightweight edges-only read for the settle-reconcile. Avoids pulling the
  // full flow (~470KB of node data) just to re-sync the edge set.
  static getFlowEdges = (
    id: string,
    versionId?: string,
    signal?: AbortSignal,
  ): Promise<IGetOneResponse<{ edges: any[] }>> => {
    const params: Record<string, string> = {};
    if (versionId) params.versionId = versionId;
    return this.get(
      `${this.pathName}/${id}/edges`,
      undefined,
      signal,
      Object.keys(params).length ? params : undefined,
    );
  };

  static getPublicFlow = (
    id: string,
    signal?: AbortSignal,
  ): Promise<IGetOneResponse<any>> => {
    return this.get(`${this.pathName}/public/${id}`);
  };

  static createFlow = (body: any): Promise<IGetOneResponse<any>> => {
    return this.post(`${this.pathName}`, body);
  };

  static duplicateFlow = (body: any): Promise<IGetOneResponse<any>> => {
    return this.post(`${this.pathName}/duplicate`, body);
  };

  static shareFlow = (id: string, body: any): Promise<IGetOneResponse<any>> => {
    return this.post(`${this.pathName}/share/${id}`, body);
  };

  static nodesAutomation = (
    id: string,
    body: any,
  ): Promise<IGetOneResponse<any>> => {
    return this.post(`${this.pathName}/nodes-automation/${id}`, body);
  };

  static copyTemplate = (
    flowId: string,
    folderId: string,
    name: string,
  ): Promise<IGetOneResponse<any>> => {
    return this.get(
      `${this.pathName}/use-template?flowId=${flowId}&folderId=${folderId}&name=${name}`,
    );
  };

  static updateFlow = (
    id: string,
    body: any,
  ): Promise<IGetOneResponse<any>> => {
    return this.patch(`${this.pathName}/${id}`, body);
  };

  static clearChatFlow = (
    id: string,
    sessionId: string,
  ): Promise<IGetOneResponse<any>> => {
    return this.patch(`${this.pathName}/clear-chat/${id}/${sessionId}`, {});
  };

  static deleteFlow = (id: string, type?: string) => {
    // return this.delete(`${this.pathName}/${id}`);
    let url = `${this.pathName}/${id}`;
    if (type === 'chat') {
      url += `?type=${type}`;
    }
    return this.delete(url);
  };

  static addNode = (
    flowId: string,
    body: any,
  ): Promise<IGetOneResponse<any>> => {
    return this.patch(`${this.pathName}/add-node/${flowId}`, body);
  };

  static updateEdge = (
    id: string,
    body: any,
  ): Promise<IGetOneResponse<any>> => {
    return this.patch(`${this.pathName}/update-edge/${id}`, body);
  };

  static onConnectEdge = (
    flowId: string,
    body: any,
  ): Promise<IGetOneResponse<any>> => {
    return this.patch(`${this.pathName}/on-connect-edge/${flowId}`, body);
  };

  static updateNode = (
    id: string,
    body: any,
  ): Promise<IGetOneResponse<any>> => {
    return this.patch(`${this.pathName}/update-node/${id}`, body);
  };

  static updateNodeData = (
    id: string,
    body: any,
  ): Promise<IGetOneResponse<any>> => {
    if (import.meta.env.DEV) {
      console.debug('[FlowService.updateNodeData]', { id, body });
    }
    return this.patch(`${this.pathName}/update-node-data/${id}`, body);
  };

  static deleteEdge = (body: {
    flowId: string;
    edgeId: string;
    versionId?: string;
  }) => {
    const params = new URLSearchParams({
      flowId: body.flowId,
      edgeId: body.edgeId,
    });
    if (body.versionId) params.set('versionId', body.versionId);
    return this.delete(`${this.pathName}/edge?${params.toString()}`);
  };

  static clearSessions = (flowId: string): Promise<IGetOneResponse<any>> => {
    return this.post(`${this.pathName}/clear-session/${flowId}`);
  };

  static deleteNode = (body: {
    flowId: string;
    nodeId: string;
    versionId?: string;
  }) => {
    const params = new URLSearchParams({
      flowId: body.flowId,
      nodeId: body.nodeId,
    });
    if (body.versionId) params.set('versionId', body.versionId);
    return this.delete(`${this.pathName}/node?${params.toString()}`);
  };

  static searchFlow = (flowName: string): Promise<IGetAllResponse<any>> => {
    return this.get(`${this.pathName}?flowName=${flowName}`);
  };

  static lastSeen = (page: number = 1): Promise<IGetAllResponse<any>> => {
    return this.get(`${this.pathName}?lastSeen=true&page=${page}&pageSize=25`);
  };

  static getSharedFlows = (page: number = 1): Promise<IGetAllResponse<any>> => {
    return this.get(`${this.pathName}/shared-flows?page=${page}&pageSize=25`);
  };

  static getFluxBots = (): Promise<IGetOneResponse<IFluxBotModel[]>> => {
    return ApiService.get(`${this.pathName}/flux-bots`);
  };

  static executeFromCanvas = (
    flowId: string,
    body: Record<string, unknown>,
    sessionId?: string,
    versionId?: string,
  ): Promise<IGetOneResponse<{ execId: string }>> => {
    const params = new URLSearchParams({ flowId });
    if (sessionId) params.set('sessionId', sessionId);
    if (versionId) params.set('versionId', versionId);
    return this.post(
      `/flux/execute-from-canvas?${params.toString()}`,
      body,
    );
  };

  static executeSingleNode = (
    flowId: string,
    nodeId: string,
    sessionId?: string,
    versionId?: string,
  ): Promise<IGetOneResponse<{ execId: string }>> => {
    const params = new URLSearchParams({ flowId, nodeId });
    if (sessionId) params.set('sessionId', sessionId);
    if (versionId) params.set('versionId', versionId);
    return this.post(`/flux/execute-single-node?${params.toString()}`, {});
  };

  static getNodeExecution = (
    execId: string,
    nodeId: string,
  ): Promise<IGetOneResponse<Record<string, unknown>>> => {
    return this.get(`/node-executions/exec/${execId}/node/${nodeId}`);
  };
}
